import { useState, useEffect } from 'react';

/**
 * 提供自動更新的當前時間狀態
 *
 * 這個 Hook 創建一個每分鐘自動更新的時間狀態，供需要時間敏感更新的組件使用。
 * 使用 State 機制而非 Ref 機制，確保時間變化能觸發 React 重新渲染。
 *
 * @param intervalMs 更新間隔（毫秒），預設 60000 (1分鐘)
 * @returns 當前本地時間的 Date 物件
 *
 * @example
 * // 基本用法
 * const currentTime = useCurrentTime(); // 每分鐘自動更新
 * const hour = currentTime.getHours(); // 本地時區的小時
 *
 * @example
 * // 自訂更新間隔
 * const currentTime = useCurrentTime(30000); // 每 30 秒更新
 *
 * @example
 * // 在 useMemo 中使用
 * const isEveningTime = useMemo(() => {
 *   const hour = currentTime.getHours();
 *   return hour >= 19 && hour < 24;
 * }, [currentTime]); // 每分鐘重新計算
 */
export const useCurrentTime = (intervalMs: number = 60000): Date => {
  // 初始化時間狀態（使用函數初始化避免每次渲染都創建 Date 物件）
  const [currentTime, setCurrentTime] = useState<Date>(() => new Date());

  useEffect(() => {
    // 每 intervalMs 毫秒更新時間
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, intervalMs);

    // 頁面可見性變化時立即更新（緩解計時器漂移問題）
    // 當用戶切換回分頁時，確保時間立即更新到最新狀態
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCurrentTime(new Date());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 清理函數：卸載時清除計時器和事件監聽器
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [intervalMs]);

  return currentTime;
};
